import { isoDateToLong, num } from "@sts/doc-templates";

/**
 * Number and date formatting, imported from the document templates rather than
 * written again.
 *
 * `packages/doc-templates/src/render.ts` already says why these are
 * locale-independent: the document engine hashes its output, so `toLocaleString`
 * would make the same survey render differently on two machines. The portal has
 * no such constraint of its own — but a heat loss shown as "6,240 W" on screen
 * and "6.240 W" in the PDF beside it is exactly the discrepancy an assessor
 * picks up on, so the two share one definition.
 */
export { isoDateToLong, num };

export const watts = (w: number): string => `${num(w, 0)} W`;
export const wattsPerM2 = (w: number): string => `${num(w, 1)} W/m²`;
export const degrees = (c: number): string => `${num(c, 1)} °C`;
export const squareMetres = (m2: number): string => `${num(m2, 2)} m²`;
export const cubicMetres = (m3: number): string => `${num(m3, 2)} m³`;
/** Thermal transmittance. Two decimals: 0.28 and 0.3 are different walls. */
export const uValue = (u: number): string => `${num(u, 2)} W/m²K`;
export const airChanges = (ach: number): string => `${num(ach, 2)} ach`;

/**
 * A timestamp as a date, for screens that never need the time of day.
 *
 * Everything the API returns from a `date` column arrives as midnight UTC, so
 * the ISO prefix is the calendar day the database meant. Passing it through a
 * local-time `Date` would show the day before to anyone west of Greenwich.
 */
export function isoDay(value: string): string {
  return value.slice(0, 10);
}

export function longDate(value: string): string {
  return isoDateToLong(isoDay(value));
}

/**
 * A timestamp with its time of day, and the zone said out loud.
 *
 * Sliced rather than passed through `Date`, for the reason above and one more
 * that matters on the evidence screens: a photograph timestamped in the
 * reader's local zone, shown beside a document timestamped in UTC, is exactly
 * the discrepancy that starts an argument at an assessment. The suffix is not
 * decoration — an unlabelled 09:00 is a claim nobody can check.
 */
export function utcMoment(iso: string): string {
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;
}

/** Percentage from a 0-1 fraction, for scan confidence. */
export function percent(fraction: number): string {
  return `${num(fraction * 100, 0)}%`;
}

/** "3 days", "1 day" — used wherever a day count is written out. */
export function days(count: number): string {
  return `${num(count, 0)} ${Math.abs(count) === 1 ? "day" : "days"}`;
}

/** Turns `survey-booked` into `Survey booked`, for enum values shown as text. */
export function humanise(slug: string): string {
  const spaced = slug.replace(/-/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
